import { useState } from "react";
import { Alert, Badge, Group, Loader, Stack, Text, Title, Tooltip } from "@mantine/core";
import { IconAlertTriangle, IconDatabase } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useTranslationJob } from "@hooks/translation/useTranslationJob";
import { useJobCost, formatUsd } from "@hooks/translation/useJobCost";
import { StatusBadge } from "@components/StatusBadge";
import { ResultCard } from "./ResultCard";
import { AllModelsDrawer } from "./AllModelsDrawer";

interface Props {
  translationId: string;
  targetLangs: string[];
}

export const TranslationResults: React.FC<Props> = ({ translationId, targetLangs }) => {
  const { t } = useTranslation();
  const { response, polling, error, timedOut } = useTranslationJob(translationId);
  const [modelsLang, setModelsLang] = useState<string | null>(null);

  const completedCount = targetLangs.filter(
    (l) => response?.translations?.[l]?.status === "completed",
  ).length;

  // Cost is recorded once the job finishes, so only fetch after polling stops.
  const isDone = !polling && response != null;
  const { totalCostUsd, totalTokens, settled } = useJobCost(translationId, isDone);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={4} ff="heading">
            {t("translation.results")}
          </Title>
          {response && !polling && <StatusBadge status={response.global_status} size="md" />}
          {isDone && totalCostUsd != null && (
            <Tooltip label={t("translation.costTokens", { tokens: totalTokens.toLocaleString() })}>
              <Badge size="md" variant="light" color="teal">
                {t("translation.cost")}: {formatUsd(totalCostUsd)}
              </Badge>
            </Tooltip>
          )}
          {isDone && settled && totalCostUsd == null && (
            <>
              <Tooltip label={t("translation.costCachedTooltip")}>
                <Badge
                  size="md"
                  variant="light"
                  color="blue"
                  leftSection={<IconDatabase size={12} />}
                >
                  {t("translation.cached")}
                </Badge>
              </Tooltip>
              <Tooltip label={t("translation.costCachedTooltip")}>
                <Badge size="md" variant="light" color="gray">
                  {t("translation.cost")}: —
                </Badge>
              </Tooltip>
            </>
          )}
        </Group>
        {polling ? (
          <Group gap={6} c="dimmed">
            <Loader size={14} />
            <Text size="sm">{t("translation.translating")}</Text>
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            {t("translation.completedOf", { done: completedCount, total: targetLangs.length })}
          </Text>
        )}
      </Group>

      {error && <Alert color="red">{error}</Alert>}

      {timedOut && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
          {t("translation.timeout")}
        </Alert>
      )}

      <Stack gap="md">
        {targetLangs.map((lang) => (
          <ResultCard
            key={lang}
            translationId={translationId}
            lang={lang}
            data={response?.translations?.[lang]}
            onShowModels={setModelsLang}
          />
        ))}
      </Stack>

      <AllModelsDrawer
        translationId={translationId}
        targetLang={modelsLang}
        onClose={() => setModelsLang(null)}
      />
    </Stack>
  );
};
